/**
 * 表单字段（PRD §8.7「表单」；platform REQ-8.7-010）：
 * - 每个输入项都有可见标签（<label> 与输入框关联）；
 * - 错误提示写在对应字段旁（aria-invalid + aria-describedby 指向错误文字），内容由调用方给出"怎么改"；
 *   服务端 VALIDATION_FAILED.params.fields 列出的字段用 ApiError.fields 取出后传给对应 Field；
 * - 输入框文字 16px 起（iOS 不因聚焦放大页面），高度 ≥44px（PRD §8.7「可点区域」）。
 * 输入组件（TextInput、CodeInput）在 Field 内自动取得 id 与 aria 属性；也可用 useFieldControl() 接入自定义控件。
 */
import { createContext, forwardRef, useContext, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cx } from '../cx.js';

export interface FieldControl {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
}

const FieldContext = createContext<FieldControl | null>(null);

/** Field 内的输入控件用它取得关联属性；不在 Field 内时返回 null。 */
export function useFieldControl(): FieldControl | null {
  return useContext(FieldContext);
}

export interface FieldProps {
  /** 可见标签 */
  label: ReactNode;
  /** 输入说明（字段下方，错误时仍保留） */
  hint?: ReactNode;
  /** 错误提示：说明怎么改；有值时字段标为无效 */
  error?: ReactNode;
  /** 指定输入框 id（默认自动生成） */
  id?: string;
  children: ReactNode;
  className?: string;
}

export function Field({ label, hint, error, id, children, className }: FieldProps) {
  const autoId = useId();
  const controlId = id ?? `${autoId}-input`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  const control: FieldControl = {
    id: controlId,
    ...(describedBy ? { 'aria-describedby': describedBy } : {}),
    ...(error ? { 'aria-invalid': true as const } : {}),
  };
  return (
    <div className={cx('flex flex-col gap-1.5', className)} data-invalid={error ? '' : undefined}>
      <label htmlFor={controlId} className="text-sm font-medium text-fg">
        {label}
      </label>
      <FieldContext.Provider value={control}>{children}</FieldContext.Provider>
      {error && (
        <p id={errorId} className="flex items-start gap-1.5 text-sm text-status-danger-fg" aria-live="polite">
          <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-status-danger" />
          <span>{error}</span>
        </p>
      )}
      {hint && (
        <p id={hintId} className="text-sm text-fg-muted">
          {hint}
        </p>
      )}
    </div>
  );
}

export const INPUT_CLASS =
  'min-h-touch w-full rounded-xl border border-border-strong bg-surface px-3 text-base text-fg placeholder:text-fg-muted ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-not-allowed disabled:opacity-50 ' +
  'aria-[invalid=true]:border-status-danger';

export type TextInputProps = InputHTMLAttributes<HTMLInputElement>;

/** 文本输入框；放在 Field 内时自动关联标签与错误提示。 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput({ className, type = 'text', ...rest }, ref) {
  const control = useFieldControl();
  return <input ref={ref} type={type} {...control} {...rest} className={cx(INPUT_CLASS, className)} />;
});
